/**
 * Agent — one enemy soldier.
 *
 * Owns the body (a capsule moved with `physics.capsuleMove`), the aim turret,
 * the weapon, cover selection, path following and damage/death. Behaviours
 * never move the agent directly: they set intent, and this turns intent into
 * motion, aim and fire.
 *
 * Combat fairness lives here and is deliberate:
 *   - the aim is a turn-rate limited turret with acceleration, so it never
 *     snaps onto a strafing player,
 *   - a reaction delay runs before the first shot of a fresh contact,
 *   - accuracy starts poor and converges over a burst,
 *   - the first shots of a fresh contact carry a deliberate miss offset, so
 *     the player always gets a beat to react.
 * Difficulty moves those three numbers. It never touches hit points.
 *
 * owner: ai
 */
import * as THREE from 'three';
import { makeRng, clamp } from '../core/Rand.js';
import { Perception, ALERT_COMBAT } from './Perception.js';
import { BehaviourRunner } from './Behavior.js';
import { steer, AGENT_RADIUS, AGENT_HEIGHT, MAX_STEP, LINK_VAULT, LINK_DROP } from './Nav.js';
import { castRay, newRayResult } from './Ray.js';
import { ROLE_HOLD } from './Squad.js';

/**
 * Killfeed names. `enemy_7` is a debug label, not something a player should ever
 * read; the UI upper-cases `entity.name` straight into the feed.
 */
const CALLSIGNS = [
  'VOLKOV', 'SEROV', 'ORLOV', 'BATURIN', 'KOVAC', 'DRAGAN', 'MEHDI', 'RASHID',
  'AZAROV', 'PETROV', 'LUKIN', 'SAMARIN', 'YUSEF', 'ISMAIL', 'GORAN', 'TARIQ',
];
const RANKS = ['RFN', 'CPL', 'SGT'];

export const DIFFICULTY = {
  recruit: {
    reaction: [0.70, 1.05], spreadStart: 8.0, spreadEnd: 2.6, converge: 1.8,
    aggression: 0.55, perception: 0.70, turnRate: 3.0, turnAccel: 11, graceShots: 3,
    burst: [3, 5], burstPause: [0.85, 1.7], damage: 7,
  },
  regular: {
    reaction: [0.42, 0.72], spreadStart: 6.0, spreadEnd: 1.35, converge: 1.15,
    aggression: 0.85, perception: 1.00, turnRate: 4.2, turnAccel: 16, graceShots: 2,
    burst: [3, 6], burstPause: [0.65, 1.25], damage: 10,
  },
  veteran: {
    reaction: [0.28, 0.48], spreadStart: 4.4, spreadEnd: 0.70, converge: 0.85,
    aggression: 1.0, perception: 1.30, turnRate: 5.6, turnAccel: 22, graceShots: 1,
    burst: [4, 7], burstPause: [0.5, 0.95], damage: 13,
  },
};

/**
 * Our animation intents mapped onto the CHARACTERS rig's clip vocabulary.
 * `null` means "the rig has no such clip and does not need one".
 */
const CHAR_ANIM = {
  idle: 'idle', walk: 'idle', run: 'idle', alert: 'idle', panic: 'idle',
  reload: 'reload', vault: 'vault', drop: 'vault', throw: 'throw',
  point: 'point', fire: 'fire',
};

const MAG = 30;
const RELOAD_TIME = 2.55;
const ROF = 0.092;                 // seconds per round
const MAX_SHOT_RANGE = 140;
const HALF_H = (AGENT_HEIGHT - 2 * AGENT_RADIUS) * 0.5;
/** Metres of separation a squad keeps between two chosen cover spans. */
const MIN_SPACING = 2.2;

const _v = new THREE.Vector3();
const _v2 = new THREE.Vector3();
const _dir = new THREE.Vector3();
const _muzzle = new THREE.Vector3();
const _hitP = new THREE.Vector3();
const _imp = new THREE.Vector3();

let nextId = 1;

export class Agent {
  constructor(world, opts = {}) {
    this.world = world;                 // { ctx, physics, nav, mask, events }
    this.id = nextId++;
    this.rng = makeRng(opts.seed ?? (0xA1E0 + this.id * 7919));

    this.difficulty = DIFFICULTY[opts.difficulty || 'regular'] || DIFFICULTY.regular;
    this.difficultyName = opts.difficulty || 'regular';

    /* ---- identity, as the UI consumes it (CONTRACTS: ai:spawn / damage:dealt) ---- */
    this.team = opts.team || 'enemy';
    this.hostile = opts.hostile !== false;
    this.faction = opts.faction || 'opfor';
    const rank = RANKS[this.difficultyName === 'veteran' ? 2 : this.difficultyName === 'recruit' ? 0 : 1];
    this.callsign = CALLSIGNS[(this.id - 1) % CALLSIGNS.length];
    this.name = `${rank} ${this.callsign}`;

    this.pos = new THREE.Vector3().copy(opts.position || _v.set(0, 0, 0));
    /**
     * `position` is the name the HUD, minimap and markers read (see
     * `HUD._maybeAlly` and `Markers.draw`); `pos` is the name the whole AI module
     * has always used. One vector, two names, so neither side has to change.
     */
    Object.defineProperty(this, 'position', {
      get: () => this.pos,
      set: (v) => { if (v) this.pos.copy(v); },
      enumerable: true, configurable: true,
    });
    /** One pooled ray result per agent — cover scoring casts once per candidate. */
    this._ray = newRayResult();
    this.vel = new THREE.Vector3();
    this.grounded = true;
    this.alive = true;
    this.health = 100;
    this.maxHealth = 100;
    this.stance = 'stand';
    this.stanceBlend = 1;
    this.sprint = false;

    this.yaw = opts.yaw ?? 0;
    this.aimYaw = this.yaw;
    this.aimPitch = 0;
    this.aimYawVel = 0;
    this.aimPitchVel = 0;
    this.aimPoint = new THREE.Vector3().copy(this.pos).add(_v.set(0, 1.5, 0));
    this.lookPoint = new THREE.Vector3().copy(this.aimPoint);
    this.aimError = Math.PI;

    /* ---- weapon ---- */
    this.ammo = MAG;
    this.magSize = MAG;
    this.reloading = false;
    this.reloadTimer = 0;
    this.fireIntent = 'hold';
    this.firing = false;
    this.fireTimer = 0;
    this.burstLeft = 0;
    this.burstPause = 0;
    this.contactTimer = -1;             // counts up once a target is acquired
    this.reactionDelay = 0;
    this.shotsThisContact = 0;
    this.spread = this.difficulty.spreadStart * Math.PI / 180;
    this.shotsFired = 0;
    this.grenades = opts.grenades ?? 1;
    this.grenadeTimer = 0;
    this.grenadeThrown = false;
    this.lastGrenadeAt = -999;

    /* ---- knowledge ---- */
    this.perception = new Perception(this, { });
    this.behaviour = new BehaviourRunner(this);
    this.squad = null;
    this.role = ROLE_HOLD;
    this.suppressedBy = 0;
    this.flankNode = -1;
    this.flankDone = false;
    this.searchLook = 0;
    this.idlePhase = 0;

    /* ---- navigation ---- */
    this.path = null;
    this.pathIndex = 0;
    this.pathKinds = null;
    this.goal = new THREE.Vector3().copy(this.pos);
    this.hasGoal = false;
    this.moveSpeedScale = 1;
    this.repathTimer = 0;
    this.pathPending = false;
    this.stuckTimer = 0;
    this.offMeshTimer = 0;

    /* ---- cover ---- */
    this.coverNode = -1;
    this.coverPos = new THREE.Vector3();
    this.coverIsHigh = false;
    this.inCover = false;
    this.coverStale = 0;
    this.peekPhase = 0;
    this.peeking = false;
    this.peekTimer = 0;
    this.lean = 0;

    /* ---- patrol ---- */
    this.patrolPoints = opts.patrol || [];
    this.patrolIndex = 0;

    /* ---- presentation ---- */
    this.character = null;
    this.hitboxes = [];
    this.ragdoll = null;
    this.calloutQueue = null;
    this.lastCalloutAt = -999;
    this.animName = 'idle';

    this.debugColor = 0x00ff88;
  }

  /* ---------------------------------------------------------------- *
   * body helpers
   * ---------------------------------------------------------------- */

  get height() { return AGENT_HEIGHT - (1 - this.stanceBlend) * 0.62; }

  eyePos(out = _v) {
    return out.set(this.pos.x + this._leanX(), this.pos.y + this.height - 0.16, this.pos.z + this._leanZ());
  }
  chestPos(out = _v) {
    return out.set(this.pos.x, this.pos.y + this.height * 0.6, this.pos.z);
  }
  _leanX() { return Math.cos(this.aimYaw) * this.lean * 0.34; }
  _leanZ() { return -Math.sin(this.aimYaw) * this.lean * 0.34; }

  /** Muzzle position: from the character rig's weapon when there is one. */
  muzzlePos(out = _muzzle) {
    const c = this.character;
    if (c && typeof c.weaponTransform === 'function') {
      // weaponTransform gives the weapon's origin; walk forward down the bore
      c.weaponTransform(out, null);
      const cp = Math.cos(this.aimPitch);
      out.x += Math.sin(this.aimYaw) * 0.46 * cp;
      out.z += Math.cos(this.aimYaw) * 0.46 * cp;
      out.y += Math.sin(this.aimPitch) * 0.46;
      return out;
    }
    if (c && typeof c.muzzle === 'function') return c.muzzle(out);
    this.eyePos(out);
    out.x += Math.sin(this.aimYaw) * 0.35;
    out.z += Math.cos(this.aimYaw) * 0.35;
    out.y -= 0.10;
    return out;
  }

  facingDot(dir) {
    const fx = Math.sin(this.aimYaw), fz = Math.cos(this.aimYaw);
    const h = Math.hypot(dir.x, dir.z);
    if (h < 1e-6) return 0;
    return (dir.x * fx + dir.z * fz) / h;
  }

  beliefPos(out = _v2) {
    if (this.squad && this.squad.confidence >= this.perception.confidence) out.copy(this.squad.playerPos);
    else out.copy(this.perception.lastKnown);
    return out;
  }

  /* ---------------------------------------------------------------- *
   * intent API used by behaviours
   * ---------------------------------------------------------------- */

  moveTo(target, speedScale = 1) {
    this.goal.copy(target);
    this.hasGoal = true;
    this.moveSpeedScale = speedScale;
    this.world.requestPath(this);
  }

  moveToNode(n, speedScale = 1) {
    if (n < 0 || !this.world.nav.ready) return;
    this.world.nav.position(n, _v);
    this.moveTo(_v, speedScale);
  }

  moveAwayFrom(p, dist) {
    _v.copy(this.pos).sub(p);
    _v.y = 0;
    if (_v.lengthSq() < 1e-6) _v.set(1, 0, 0);
    _v.normalize().multiplyScalar(dist).add(this.pos);
    const nav = this.world.nav;
    if (nav.ready) {
      const n = nav.nearest(_v);
      if (n >= 0) { this.moveToNode(n, 1); return; }
    }
    this.moveTo(_v, 1);
  }

  stopMoving() {
    this.hasGoal = false;
    this.path = null;
    this.pathIndex = 0;
  }

  arrived() {
    if (!this.hasGoal) return true;
    if (this.pathPending) return false;
    const dx = this.pos.x - this.goal.x, dz = this.pos.z - this.goal.z;
    return (dx * dx + dz * dz) < 0.42 * 0.42;
  }

  setStance(s) { this.stance = s; }
  setFireIntent(m) { this.fireIntent = m; }
  aimAt(p) { this.aimPoint.copy(p); this.lookPoint.copy(p); }
  faceTarget() {
    const c = this.perception.hasLOS ? this.perception.losPoint : this.beliefPos(_v2);
    this.aimPoint.copy(c);
    this.lookPoint.copy(c);
  }
  faceMove() {
    const t = this._currentCorner(_v2);
    if (t) {
      this.aimPoint.set(t.x, this.pos.y + 1.5, t.z);
      // eyes still track the threat while the body runs
      if (this.perception.confidence > 0.3) this.lookPoint.copy(this.beliefPos());
      else this.lookPoint.copy(this.aimPoint);
    } else this.faceTarget();
  }
  lookAround(phase) {
    const r = 8;
    this.aimPoint.set(
      this.pos.x + Math.sin(this.yaw + Math.sin(phase) * 0.85) * r,
      this.pos.y + 1.55,
      this.pos.z + Math.cos(this.yaw + Math.sin(phase) * 0.85) * r);
    this.lookPoint.copy(this.aimPoint);
  }
  /**
   * The CHARACTERS rig has its own clip vocabulary; locomotion is driven by
   * `setLocomotion`, so a 'run' request is just "stop leaning and stand up".
   */
  playAnim(name, opts) {
    this.animName = name;
    const c = this.character;
    if (!c || !c.play) return;
    const mapped = CHAR_ANIM[name];
    if (mapped === null) return;
    try { c.play(mapped || name, opts); } catch { /* an unknown clip is not fatal */ }
  }

  needsReload() { return this.ammo <= 0 || (this.ammo < 7 && this.inCover && !this.perception.hasLOS); }
  beginReload() {
    if (this.reloading || this.ammo === this.magSize) return;
    this.reloading = true;
    this.reloadTimer = RELOAD_TIME;
    this.playAnim('reload');
  }

  canThrowGrenade() {
    return this.alive && this.grenades > 0 && (this.world.time - this.lastGrenadeAt) > 16;
  }

  throwGrenade() {
    if (this.grenades <= 0) return;
    this.grenades--;
    this.grenadeThrown = true;
    this.lastGrenadeAt = this.world.time;
    this.world.spawnGrenade(this, this.beliefPos(_v2));
  }

  nextPatrol() {
    if (this.patrolPoints.length === 0) return;
    this.patrolIndex = (this.patrolIndex + 1) % this.patrolPoints.length;
    this.moveTo(this.patrolPoints[this.patrolIndex], 0.45);
  }

  wantCallout(type) { this.calloutQueue = type; }
  callout(type) { this.world.callout(this, type); }

  /* ---------------------------------------------------------------- *
   * cover
   * ---------------------------------------------------------------- */

  /**
   * Score the baked cover spans around an anchor and commit to the best one.
   * @returns {number} the chosen span, or -1
   */
  requestCover(opts = {}) {
    const nav = this.world.nav;
    if (!nav.ready) return -1;
    const threat = this.beliefPos(_v2).clone();
    const radius = opts.radius ?? 11;
    const minDist = opts.minDist ?? 0;
    const anchor = opts.towards
      ? _v.copy(this.pos).lerp(threat, 0.42)
      : _v.copy(this.pos);
    const selfToThreat = Math.hypot(threat.x - this.pos.x, threat.z - this.pos.z);

    let best = -1, bestScore = -Infinity;
    const bestPos = new THREE.Vector3();
    nav.forEachCoverNear(anchor.x, anchor.z, this.pos.y, radius, (n) => {
      const nx = nav.px[n], ny = nav.py[n], nz = nav.pz[n];
      const cover = nav.coverFrom(n, threat.x - nx, threat.z - nz);
      if (cover < 1) return;
      const travel = Math.hypot(nx - this.pos.x, nz - this.pos.z) + Math.abs(ny - this.pos.y) * 1.5;
      if (travel < minDist) return;
      if (this.squad && this.squad.isClaimed(n, this)) return;
      const toThreat = Math.hypot(nx - threat.x, nz - threat.z);
      if (toThreat < 3.0) return;                       // do not hug the player

      // Tactical spacing. Two soldiers sharing one metre of parapet read as a
      // crowd, not as a squad, and one grenade would take both. `isClaimed`
      // only stops them picking the *same* span; this stops them picking
      // adjacent ones.
      if (this.squad && this.squad.tooClose(nx, nz, this, MIN_SPACING)) return;

      let score = cover * 3.2 - travel * 0.30;
      if (opts.towards) score += (selfToThreat - toThreat) * 0.85;
      if (opts.away) score += (toThreat - selfToThreat) * 0.75;
      if (opts.lateral) {
        // prefer moving sideways relative to the threat axis
        const ax = (threat.x - this.pos.x) / Math.max(1e-3, selfToThreat);
        const az = (threat.z - this.pos.z) / Math.max(1e-3, selfToThreat);
        const dx = nx - this.pos.x, dz = nz - this.pos.z;
        score += Math.abs(-az * dx + ax * dz) * 0.9 - Math.abs(ax * dx + az * dz) * 0.5;
      }
      // an engagement distance that reads on screen and plays fair
      score -= Math.abs(toThreat - 11) * 0.16;
      if (opts.sightOn) {
        // needs a line onto the threat when peeking out
        _v.set(nx, ny + 1.5, nz);
        _dir.set(threat.x - nx, (threat.y + 1.2) - (ny + 1.5), threat.z - nz);
        const d = _dir.length();
        if (d > 1e-3) {
          _dir.multiplyScalar(1 / d);
          if (castRay(this.world.physics, _v, _dir, d - 0.4, this.world.mask, this._ray)) score -= 6;
          else score += 4;
        }
      }
      if (score > bestScore) { bestScore = score; best = n; bestPos.set(nx, ny, nz); }
    });

    if (best < 0) return -1;
    this.coverNode = best;
    this.coverPos.copy(bestPos);
    this.coverIsHigh = (nav.coverHigh[best] !== 0);
    this.coverStale = 0;
    this.moveTo(bestPos, opts.speed ?? 1);
    return best;
  }

  _updateCover() {
    const nav = this.world.nav;
    if (this.coverNode < 0 || !nav.ready) { this.inCover = false; return; }
    const dx = this.pos.x - this.coverPos.x, dz = this.pos.z - this.coverPos.z;
    const near = (dx * dx + dz * dz) < 0.75 * 0.75 && Math.abs(this.pos.y - this.coverPos.y) < 0.8;
    if (!near) { this.inCover = false; return; }
    const t = this.beliefPos(_v2);
    const c = nav.coverFrom(this.coverNode, t.x - this.coverPos.x, t.z - this.coverPos.z);
    this.inCover = c >= 1;
    this.coverIsHigh = (nav.coverHigh[this.coverNode] & 0xff) !== 0;
  }

  /** Can this agent hit the target by leaning out of its current cover? */
  canPeekTarget() {
    const t = this.perception.confidence > 0.2 ? this.beliefPos(_v2) : null;
    if (!t) return false;
    return this.pos.distanceToSquared(t) < 60 * 60;
  }

  beginPeek() {
    this.peeking = true;
    this.peekTimer = 0;
    this.peekSide = this.rng() < 0.5 ? -1 : 1;
    this.peekOut = true;
    this.exposeTime = this.rng.range(1.1, 2.1);
    this.hideTime = this.rng.range(0.7, 1.5);
    this._pose('peek');
  }

  _pose(kind) {
    const c = this.character;
    if (!c || !c.play) return;
    try { c.play(kind, { side: this.peekSide < 0 ? 'left' : 'right' }); } catch { /* optional */ }
  }

  tickPeek(dt) {
    if (!this.peeking) return;
    this.peekTimer += dt;
    if (this.peekOut && this.peekTimer > this.exposeTime) {
      this.peekOut = false; this.peekTimer = 0;
      this.hideTime = this.rng.range(0.7, 1.5);
      this.burstLeft = 0;
      this.burstPause = Math.max(this.burstPause, 0.25);
      this._pose('cover');
    } else if (!this.peekOut && this.peekTimer > this.hideTime) {
      this.peekOut = true; this.peekTimer = 0;
      this.peekSide = this.rng() < 0.35 ? -this.peekSide : this.peekSide;
      this.exposeTime = this.rng.range(1.1, 2.1);
      this._pose('peek');
    }
    const want = this.peekOut ? this.peekSide : 0;
    this.lean += (want - this.lean) * Math.min(1, dt * 6);
  }

  endPeek() {
    this.peeking = false;
    this.peekOut = false;
    const c = this.character;
    if (c && c.play) { try { c.play('uncover'); } catch { /* optional */ } }
  }

  /* ---------------------------------------------------------------- *
   * per-fixed-step: aim + fire
   * ---------------------------------------------------------------- */

  fixedStep(dt) {
    if (!this.alive) return;

    this.stanceBlend += ((this.stance === 'crouch' ? 0 : 1) - this.stanceBlend) * Math.min(1, dt * 7);
    this.suppressedBy = Math.max(0, this.suppressedBy - dt * 0.45);
    this.coverStale += dt;
    if (!this.peeking) this.lean += (0 - this.lean) * Math.min(1, dt * 5);

    if (this.reloading) {
      this.reloadTimer -= dt;
      if (this.reloadTimer <= 0) {
        this.reloading = false;
        this.ammo = this.magSize;
        this.playAnim('idle');
      }
    }

    this._tickAim(dt);
    this._tickWeapon(dt);
  }

  _tickAim(dt) {
    const d = this.difficulty;
    // where the turret wants to point
    _v.copy(this.aimPoint).sub(this.eyePos(_v2));
    const wantYaw = Math.atan2(_v.x, _v.z);
    const wantPitch = Math.atan2(_v.y, Math.hypot(_v.x, _v.z));

    // suppression and sprinting both make the turret sloppier
    const rate = d.turnRate * (1 - this.suppressedBy * 0.35);
    const accel = d.turnAccel * (1 - this.suppressedBy * 0.3);

    this.aimYawVel = approachAngular(this.aimYaw, wantYaw, this.aimYawVel, rate, accel, dt);
    this.aimYaw = wrapPi(this.aimYaw + this.aimYawVel * dt);
    this.aimPitchVel = approachAngular(this.aimPitch, wantPitch, this.aimPitchVel, rate * 0.8, accel * 0.8, dt);
    this.aimPitch = clamp(this.aimPitch + this.aimPitchVel * dt, -1.1, 1.1);

    this.aimError = Math.abs(wrapPi(wantYaw - this.aimYaw)) + Math.abs(wantPitch - this.aimPitch) * 0.6;
    this.yaw = this.aimYaw;
  }

  _tickWeapon(dt) {
    const d = this.difficulty;
    this.firing = false;

    const wantFire = (this.fireIntent === 'aimed' || this.fireIntent === 'suppress')
      && this.ammo > 0 && !this.reloading
      && (!this.peeking || this.peekOut);

    if (!wantFire) {
      // losing the target resets the whole convergence, so re-engagement is
      // always fair rather than a continuation of a dialled-in burst
      if (this.contactTimer >= 0 && !this.perception.hasLOS) {
        this.contactTimer = -1;
        this.shotsThisContact = 0;
        this.spread = d.spreadStart * Math.PI / 180;
      }
      this.burstLeft = 0;
      return;
    }

    if (this.contactTimer < 0) {
      this.contactTimer = 0;
      this.reactionDelay = this.rng.range(d.reaction[0], d.reaction[1])
        * (this.perception.alert >= ALERT_COMBAT ? 0.72 : 1.0);
      this.shotsThisContact = 0;
      this.spread = d.spreadStart * Math.PI / 180;
    }
    this.contactTimer += dt;
    if (this.contactTimer < this.reactionDelay) return;

    // the turret must actually be on target before a shot leaves the barrel
    if (this.aimError > 0.10) return;

    if (this.burstPause > 0) { this.burstPause -= dt; return; }
    if (this.burstLeft <= 0) {
      this.burstLeft = Math.round(this.rng.range(d.burst[0], d.burst[1]));
      if (this.fireIntent === 'suppress') this.burstLeft += 2;
    }

    this.fireTimer -= dt;
    if (this.fireTimer > 0) return;
    this.fireTimer = ROF;

    this._fireRound();

    this.burstLeft--;
    if (this.burstLeft <= 0) {
      this.burstPause = this.rng.range(d.burstPause[0], d.burstPause[1])
        * (this.fireIntent === 'suppress' ? 1.35 : 1.0);
    }
    // accuracy converges over the burst
    const endSpread = d.spreadEnd * Math.PI / 180;
    const k = 1 - Math.exp(-ROF / Math.max(0.05, d.converge));
    this.spread += (endSpread - this.spread) * k;
  }

  _fireRound() {
    const w = this.world;
    const d = this.difficulty;
    this.ammo--;
    this.shotsFired++;
    this.shotsThisContact++;
    this.firing = true;

    this.muzzlePos(_muzzle);
    if (this.character && this.character.play) {
      try { this.character.play('fire', { power: 1 }); } catch { /* optional */ }
    }

    // --- where the shot is actually sent ---
    const suppressing = this.fireIntent === 'suppress';
    const target = _v.copy(suppressing ? this.world.suppressPointFor(this) : this.aimPoint);

    // deliberate first-contact misses: the player always gets a beat
    let spread = this.spread;
    if (this.shotsThisContact <= d.graceShots && !suppressing) {
      const off = this.rng.range(0.55, 1.35) * (this.shotsThisContact <= 1 ? 1.3 : 1.0);
      const ang = this.rng() * Math.PI * 2;
      target.x += Math.cos(ang) * off;
      target.y += Math.sin(ang) * off * 0.5;
      target.z += Math.sin(ang) * off;
    }
    if (suppressing) spread *= 1.6;

    _dir.copy(target).sub(_muzzle);
    const dist = _dir.length();
    if (dist < 1e-3) return;
    _dir.multiplyScalar(1 / dist);
    // cone perturbation, seeded
    const a = this.rng() * Math.PI * 2;
    const r = Math.sqrt(this.rng()) * spread;
    perturb(_dir, a, r);

    w.resolveShot(this, _muzzle, _dir, d.damage);
  }

  /* ---------------------------------------------------------------- *
   * movement (runs at a reduced rate, see AI.js)
   * ---------------------------------------------------------------- */

  _currentCorner(out) {
    if (!this.path || this.pathIndex >= this.path.length) return null;
    return out.copy(this.path[this.pathIndex]);
  }

  movementStep(dt, agents, selfIndex) {
    if (!this.alive) return;
    const phys = this.world.physics;
    const nav = this.world.nav;

    let desiredSpeed = 0;
    const corner = this._currentCorner(_v2);
    if (corner && this.hasGoal) {
      const dx = corner.x - this.pos.x, dz = corner.z - this.pos.z;
      const dsq = dx * dx + dz * dz;
      const dy = corner.y - this.pos.y;
      const isOffMesh = this.pathKinds && this.pathKinds[this.pathIndex] > 0;
      const tol = isOffMesh ? 0.55 : 0.55;
      if (dsq < tol * tol && Math.abs(dy) < 1.9) {
        // off-mesh links are traversed explicitly, with the vault animation
        if (isOffMesh && this.offMeshTimer <= 0) {
          this.offMeshTimer = this.pathKinds[this.pathIndex] === LINK_VAULT ? 0.55 : 0.32;
          this.playAnim(this.pathKinds[this.pathIndex] === LINK_VAULT ? 'vault' : 'drop');
        }
        this.pathIndex++;
        if (this.pathIndex >= this.path.length) { this.path = null; this.hasGoal = false; }
      } else {
        const base = this.sprint ? 5.6 : (this.stance === 'crouch' ? 1.5 : 3.6);
        desiredSpeed = base * this.moveSpeedScale;
      }
    }

    if (this.offMeshTimer > 0) {
      this.offMeshTimer -= dt;
      desiredSpeed = Math.max(desiredSpeed, 2.2);
    }

    // steering
    if (desiredSpeed > 0.02 && corner) {
      steer(_v, this.pos, corner, desiredSpeed, agents, nav, selfIndex);
      this.vel.x = _v.x; this.vel.z = _v.z;
    } else {
      this.vel.x *= 0.55; this.vel.z *= 0.55;
      if (Math.abs(this.vel.x) < 0.02) this.vel.x = 0;
      if (Math.abs(this.vel.z) < 0.02) this.vel.z = 0;
    }

    const moving = Math.abs(this.vel.x) > 0.01 || Math.abs(this.vel.z) > 0.01;
    if (!moving && this.grounded && this.offMeshTimer <= 0) {
      // parked: skip the sweep entirely, this is most of the squad most of the time
      this.vel.y = 0;
      return;
    }

    this.vel.y -= 21.6 * dt;
    if (this.vel.y < -32) this.vel.y = -32;

    const hh = HALF_H;
    _v.set(this.pos.x, this.pos.y + AGENT_RADIUS + hh, this.pos.z);
    _v2.set(this.vel.x * dt, this.vel.y * dt, this.vel.z * dt);
    const r = phys.capsuleMove(_v, hh, AGENT_RADIUS, _v2, {
      mask: this.world.mask, stepHeight: MAX_STEP, grounded: this.grounded,
    });
    const before = this.pos.x + this.pos.z;
    this.pos.set(r.position.x, r.position.y - AGENT_RADIUS - hh, r.position.z);
    this.grounded = r.grounded;
    if (this.grounded && this.vel.y < 0) this.vel.y = 0;

    // stuck detection: a blocked agent re-paths rather than grinding a wall
    if (moving && Math.abs((this.pos.x + this.pos.z) - before) < 0.004) {
      this.stuckTimer += dt;
      if (this.stuckTimer > 0.85) {
        this.stuckTimer = 0;
        this.world.requestPath(this, true);
      }
    } else this.stuckTimer = 0;
  }

  /* ---------------------------------------------------------------- *
   * damage / death
   * ---------------------------------------------------------------- */

  /** Called by the weapons module through `mesh.userData.entity`. */
  applyDamage(amount, dir, zone) {
    if (!this.alive || !Number.isFinite(amount) || amount <= 0) return this.health;
    // the CHARACTERS rig's region vocabulary (see REGION_BONE in their Rig.js)
    const region = zone === 'head' ? 'head'
      : zone === 'leg' ? 'legR'
      : zone === 'foot' ? 'footR'
      : zone === 'arm' ? 'armR'
      : 'torso';
    this.health -= amount;
    const killed = this.health <= 0;

    _imp.copy(dir && dir.isVector3 ? dir : _v.set(0, 0, 1)).normalize()
      .multiplyScalar(killed ? 26 : 6);

    try {
      this.world.ctx.events?.emit('damage:dealt', {
        entity: this, amount, headshot: region === 'head', killed,
        // the killfeed reads `entity.name`; these are here so a listener never
        // has to reach into an Agent to render a line
        name: this.name, team: this.team, hostile: this.hostile, position: this.pos,
      });
    } catch { /* listeners must never break the sim */ }

    // the fight gets loud and the squad reacts to one of theirs being hit
    this.suppressedBy = Math.min(1, this.suppressedBy + 0.5);
    if (this.squad && !this.squad.engaged && this.perception.confidence < 0.5) {
      this.perception.awareness = 1;
      this.perception.confidence = Math.max(this.perception.confidence, 0.7);
      if (this.world.ctx.player) {
        this.perception.lastKnown.copy(this.world.ctx.player.position);
      }
      this.callout('contact');
    }

    if (this.character && this.character.hit && !killed) {
      try { this.character.hit(region, _imp, null); } catch { /* proxy tolerant */ }
    }

    if (killed) this.die(_imp, region);
    return this.health;
  }

  die(impulse, boneName) {
    if (!this.alive) return;
    this.alive = false;
    this.health = 0;
    this.stopMoving();
    this.setFireIntent('hold');
    this.behaviour.reset();
    this.world.onDeath(this, impulse || _imp.set(0, 0, 0), boneName || 'torso');
  }

  reset(opts = {}) {
    this.alive = true;
    this.health = this.maxHealth;
    this.pos.copy(opts.position || this.pos);
    this.vel.set(0, 0, 0);
    this.yaw = opts.yaw ?? this.yaw;
    this.aimYaw = this.yaw; this.aimPitch = 0;
    this.aimYawVel = 0; this.aimPitchVel = 0;
    this.ammo = this.magSize;
    this.reloading = false;
    this.grenades = 1;
    this.grenadeThrown = false;
    this.lastGrenadeAt = -999;
    this.contactTimer = -1;
    this.shotsThisContact = 0;
    this.shotsFired = 0;
    this.spread = this.difficulty.spreadStart * Math.PI / 180;
    this.suppressedBy = 0;
    this.coverNode = -1; this.inCover = false; this.coverStale = 0;
    this.flankNode = -1; this.flankDone = false;
    this.role = ROLE_HOLD;
    this.lean = 0; this.peeking = false;
    this.stance = 'stand'; this.stanceBlend = 1;
    this.perception.reset();
    this.behaviour.reset();
    this.rng.reseed(opts.seed ?? (0xA1E0 + this.id * 7919));
    this.stopMoving();
  }
}

/* ------------------------------------------------------------------ */

function wrapPi(a) {
  while (a > Math.PI) a -= Math.PI * 2;
  while (a < -Math.PI) a += Math.PI * 2;
  return a;
}

/**
 * Rate- and acceleration-limited approach of an angle. This is what stops the
 * aim from teleporting onto the player.
 */
function approachAngular(cur, want, vel, maxRate, accel, dt) {
  const err = wrapPi(want - cur);
  // critically-damped-ish target velocity, clamped to the turret's max rate
  let target = clamp(err * 6.0, -maxRate, maxRate);
  // slow into the last few degrees so the turret settles instead of ringing
  if (Math.abs(err) < 0.25) target = err * 7.5;
  const dv = clamp(target - vel, -accel * dt, accel * dt);
  return vel + dv;
}

const _tan = new THREE.Vector3();
const _bit = new THREE.Vector3();
/** Rotate `dir` inside a cone of half-angle `r`, at roll `a`. Allocation free. */
function perturb(dir, a, r) {
  if (r <= 1e-6) return dir;
  const ax = Math.abs(dir.x) < 0.9 ? 1 : 0;
  _tan.set(ax, ax ? 0 : 1, 0).cross(dir).normalize();
  _bit.copy(_tan).cross(dir).normalize();
  const s = Math.tan(r), ca = Math.cos(a), sa = Math.sin(a);
  dir.x += (_tan.x * ca + _bit.x * sa) * s;
  dir.y += (_tan.y * ca + _bit.y * sa) * s;
  dir.z += (_tan.z * ca + _bit.z * sa) * s;
  return dir.normalize();
}

export { MAX_SHOT_RANGE, HALF_H, wrapPi };
